'use client'

import { useState, useEffect } from 'react'
import { Calendar, AlertCircle, Bell } from 'lucide-react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material'

interface EditRenewalDateModalProps {
  isOpen: boolean
  onClose: () => void
  onSave: (dealId: string, renewalDate: string | null) => Promise<void>
  dealId: string | null
  currentDate: string | null
  customerName?: string
}

export function EditRenewalDateModal({
  isOpen,
  onClose,
  onSave,
  dealId,
  currentDate,
  customerName
}: EditRenewalDateModalProps) {
  const [renewalDate, setRenewalDate] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      // Convert date to YYYY-MM-DD format for the input
      if (currentDate) {
        // Handle both ISO format and date-only format
        const dateOnly = currentDate.split('T')[0]
        setRenewalDate(dateOnly)
      } else {
        setRenewalDate('')
      }
      setError(null)
    }
  }, [isOpen, currentDate])

  const handleSave = async () => {
    if (!dealId) return

    setIsSaving(true)
    setError(null)

    try {
      await onSave(dealId, renewalDate || null)
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to update renewal date')
    } finally {
      setIsSaving(false)
    }
  }

  const handleClear = async () => {
    if (!dealId) return

    setIsSaving(true)
    setError(null)

    try {
      await onSave(dealId, null)
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to clear renewal date')
    } finally {
      setIsSaving(false)
    }
  }

  // Calculate days until renewal for preview
  const getDaysUntil = () => {
    if (!renewalDate) return null
    const date = new Date(renewalDate)
    const now = new Date()
    now.setHours(0, 0, 0, 0)
    date.setHours(0, 0, 0, 0)
    return Math.ceil((date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
  }

  const daysUntil = getDaysUntil()
  const previewSeverity: 'error' | 'warning' | 'info' =
    daysUntil === null ? 'info' : daysUntil <= 0 ? 'error' : daysUntil <= 30 ? 'warning' : 'info'

  const handleClose = () => {
    if (!isSaving) onClose()
  }

  return (
    <Dialog open={isOpen} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box component="span" sx={{ color: 'primary.main', display: 'flex' }}>
          <Calendar size={20} />
        </Box>
        Set AMC Renewal Date
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {customerName ? `Set the renewal date for ${customerName}'s agreement.` : 'Set when this agreement needs to be renewed.'}
          {' '}A reminder task will be created and notifications will be sent before the renewal date.
        </DialogContentText>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, py: 2 }}>
          <TextField
            id="renewalDate"
            label="Renewal Date"
            type="date"
            size="small"
            fullWidth
            value={renewalDate}
            onChange={(e) => setRenewalDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          {renewalDate && daysUntil !== null && (
            <Alert severity={previewSeverity} icon={<Bell size={16} />}>
              {daysUntil < 0 ? (
                <>This renewal is <strong>{Math.abs(daysUntil)} days overdue</strong>!</>
              ) : daysUntil === 0 ? (
                <>This renewal is <strong>due today</strong>!</>
              ) : (
                <>Renewal in <strong>{daysUntil} days</strong>. Reminder emails will be sent at 30, 14, 7, 3, and 1 day(s) before.</>
              )}
            </Alert>
          )}

          {error && (
            <Alert severity="error" icon={<AlertCircle size={16} />}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Box>
          {currentDate && (
            <Button color="error" onClick={handleClear} disabled={isSaving}>
              Clear Date
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" color="inherit" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={isSaving || !renewalDate}>
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  )
}
